import {
  vArray,
  vBoolean,
  vCurrency,
  type vInput,
  vInt,
  vMaxLength,
  vMinLength,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPicklist,
  vPipe,
  vRegex,
  vString,
  vTransform,
} from '../validation/index.ts'
import { freightClasses, hazardClasses, workflows } from './common.ts'
import { PackageTypeMapper, packagingTypes } from './packaging-type.ts'
import {
  legacyPackingGroups,
  packingGroups,
  parseLegacyPackingGroup,
} from './packing-group.ts'

const readOnlyDetailSchema = vObject({
  declaredValue: vOptional(vBoolean()),
  description: vOptional(vBoolean()),
  freightClass: vOptional(vBoolean()),
  hazardClass: vOptional(vBoolean()),
  hazardous: vOptional(vBoolean()),
  hazmatId: vOptional(vBoolean()),
  height: vOptional(vBoolean()),
  length: vOptional(vBoolean()),
  nmfc: vOptional(vBoolean()),
  nonStandard: vOptional(vBoolean()),
  package: vOptional(vBoolean()),
  packingGroup: vOptional(vBoolean()),
  pieces: vOptional(vBoolean()),
  saidToContain: vOptional(vBoolean()),
  saidToContainPackagingType: vOptional(vBoolean()),
  secondaryHazardClass: vPipe(
    vBoolean(),
    vTransform(v => !!v),
  ),
  specialPermit: vPipe(
    vBoolean(),
    vTransform(v => !!v),
  ),
  stackable: vOptional(vBoolean()),
  tertiaryHazardClass: vPipe(
    vBoolean(),
    vTransform(v => !!v),
  ),
  weight: vOptional(vBoolean()),
  width: vOptional(vBoolean()),
  properShippingName: vOptional(vBoolean()),
  technicalName: vOptional(vBoolean()),
  reportableQuantity: vOptional(vBoolean()),
  residueLastContained: vOptional(vBoolean()),
  limitedQuantity: vOptional(vBoolean()),
  marinePollutant: vOptional(vBoolean()),
})

export const hazardDetailSchema = vObject({
  hazmatId: vOptional(vPipe(vString(), vRegex(/^(NA|UN)\d{4}$/))),
  specialPermit: vOptional(vString()),
  packingGroup: vOptional(
    vPipe(
      vPicklist([
        ...legacyPackingGroups,
        ...packingGroups.map(p => p.key),
        ...packingGroups.map(p => p.name),
      ]),
      vTransform(value => parseLegacyPackingGroup(value)),
    ),
  ),
  hazardClass: vOptional(vPicklist(hazardClasses.map(c => c.key))),
  secondaryHazardClass: vOptional(
    vPicklist(hazardClasses.filter(c => c.isValidSubClass).map(c => c.key)),
  ),
  tertiaryHazardClass: vOptional(
    vPicklist(hazardClasses.filter(c => c.isValidSubClass).map(c => c.key)),
  ),
  properShippingName: vOptional(vString()),
  technicalName: vOptional(vString()),
  reportableQuantity: vOptional(vBoolean()),
  residueLastContained: vOptional(vBoolean()),
  limitedQuantity: vOptional(vBoolean()),
  marinePollutant: vOptional(vBoolean()),
})

export type ReadOnlyDetail = vOutput<typeof readOnlyDetailSchema>

const intSchema = vPipe(vNumber(), vInt())
export const handlingUnitSchema = vPipe(
  vObject({
    declaredValue: vOptional(
      vObject({
        amount: vOptional(vNumber()),
        currency: vCurrency('usd'),
      }),
    ),
    description: vOptional(vString()),
    freightClass: vOptional(vPicklist(freightClasses)),
    nmfc: vOptional(vPipe(vString(), vRegex(/^[0-9-]+$/))),
    mode: vPipe(
      vOptional(vPicklist(workflows)),
      vTransform(value => value || 'ltl'),
    ),
    height: vOptional(intSchema),
    length: vOptional(intSchema),
    weight: vOptional(intSchema),
    width: vOptional(intSchema),
    pieces: vOptional(intSchema),
    saidToContain: vOptional(intSchema),
    package: vPipe(
      vOptional(vPicklist(packagingTypes.map(p => p.key))),
      vTransform(value =>
        value ? PackageTypeMapper.coreToLegacy(value) : value,
      ),
    ),
    saidToContainPackagingType: vPipe(
      vOptional(
        vPicklist(packagingTypes.filter(p => p.canBeContained).map(p => p.key)),
      ),
      vTransform(value =>
        value ? PackageTypeMapper.coreToLegacy(value) : value,
      ),
    ),
    hazardous: vOptional(vBoolean()),
    hazard: vOptional(hazardDetailSchema),
    stackable: vOptional(vBoolean()),
    nonStandard: vOptional(vBoolean()),
    readOnly: vOptional(readOnlyDetailSchema),
    autoGenerated: vOptional(vBoolean()),
  }),
)

export type HandlingUnitPropsInput = vInput<typeof handlingUnitSchema>
export type HandlingUnitProps = vOutput<typeof handlingUnitSchema>

const handlingUnitMode = vOptionalDef(vPicklist([...workflows, 'all']), 'all')
export type HandlingUnitMode = vOutput<typeof handlingUnitMode>
export const handlingUnitSearchSchema = vObject({
  mode: handlingUnitMode,
  searchText: vOptional(vString()),
  onlyContainable: vOptional(vBoolean()),
  carrierFilter: vOptional(vString()),
  continuationToken: vOptional(vString()),
})
export type HandlingUnitSearchRequest = vOutput<typeof handlingUnitSearchSchema>

export const handlingUnitClearSchema = vObject({
  mode: handlingUnitMode,
})
export type HandlingUnitClearRequest = vOutput<typeof handlingUnitClearSchema>

export const handlingUnitBulkDeleteSchema = vObject({
  ids: vPipe(vArray(vObjectId()), vMinLength(1), vMaxLength(2000)),
})
export type HandlingUnitBulkDeleteRequest = vOutput<
  typeof handlingUnitBulkDeleteSchema
>
